import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { apiClient, formatSAR } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { FEATURES } from "../lib/features";
import {
  Building2, ShieldCheck, Search as SearchIcon, Truck, Wrench, ArrowRight,
  CheckCircle2, Sparkles, Calculator, Lock, AlertCircle, Loader2,
} from "lucide-react";

// All services are now OPTIONAL — the user is no longer forced to use any
// of them in order to complete the deal. They are presented as helpful
// add-ons, not gatekeepers.
const STEPS = [
  { id: "inspection",  icon: SearchIcon,  ar: "اختر الفحص",          en: "Inspection",  link_cat: "inspection" },
  { id: "insurance",   icon: ShieldCheck, ar: "اختر التأمين",        en: "Insurance",   link_cat: "insurance" },
  { id: "showroom",    icon: Building2,   ar: "نقل الملكية",    en: "Ownership transfer", link_cat: "ownership_transfer_official" },
  { id: "transport",   icon: Truck,       ar: "النقل والشحن",        en: "Transport",   link_cat: "transport" },
  { id: "maintenance", icon: Wrench,      ar: "الصيانة والتجهيز",    en: "Maintenance", link_cat: "maintenance" },
];

export default function DealCompletionPage() {
  const { vehicleId } = useParams();
  const { lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [vehicle, setVehicle] = useState(null);
  const [orders, setOrders] = useState([]);
  const [feeData, setFeeData] = useState(null);
  const [customPrice, setCustomPrice] = useState("");
  const [withEscrow, setWithEscrow] = useState(true); // buyer choice
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (vehicleId && vehicleId !== "general") {
      apiClient.get(`/vehicles/${vehicleId}`).then(({ data }) => setVehicle(data)).catch(() => {});
    }
    apiClient.get("/service-orders/mine").then(({ data }) => setOrders(data.items || [])).catch(() => {});
  }, [vehicleId]);

  // Whenever the vehicle (or manual price) changes, recompute the fee on the BE.
  const effectivePrice = useMemo(() => {
    const manual = Number(customPrice);
    if (Number.isFinite(manual) && manual > 0) return manual;
    return Number(vehicle?.price_sar || vehicle?.price || 0);
  }, [customPrice, vehicle]);

  useEffect(() => {
    if (!effectivePrice || effectivePrice <= 0) { setFeeData(null); return; }
    let cancelled = false;
    // When we know the vehicle, send vehicle_id so the backend looks up the
    // seller's trust score. `with_escrow` toggles the protected (150 SAR)
    // vs direct (0 SAR) flow.
    const params = vehicleId && vehicleId !== "general"
      ? { vehicle_id: vehicleId, price_sar: effectivePrice, with_escrow: withEscrow }
      : { price_sar: effectivePrice, with_escrow: withEscrow };
    apiClient.get("/deals/fee", { params })
      .then(({ data }) => { if (!cancelled) setFeeData(data); })
      .catch(() => { if (!cancelled) setFeeData(null); });
    return () => { cancelled = true; };
  }, [effectivePrice, vehicleId, withEscrow]);

  const completed = (cat) => orders.some(o => o.partner_category === cat && o.vehicle_id === vehicleId);

  const handlePay = async () => {
    setError("");
    if (!user) {
      navigate("/login", { state: { from: `/deal/${vehicleId}` } });
      return;
    }
    if (!feeData) {
      setError(lang === "ar" ? "يرجى إدخال قيمة السيارة." : "Please enter the vehicle price.");
      return;
    }
    // No-escrow direct flow → nothing to pay to CARTAL. Show a guidance
    // notice and route to the vehicle / messages so the buyer can finalise
    // directly with the seller.
    if (!withEscrow || feeData.fee_sar === 0) {
      if (vehicleId && vehicleId !== "general") {
        navigate(`/vehicle/${vehicleId}`);
      } else {
        navigate("/marketplace");
      }
      return;
    }
    setSubmitting(true);
    try {
      // iter148 — CARTAL Escrow now uses Moyasar Authorization Hold on
      // the FULL amount (car value + 75 SAR service fee). Nothing is
      // captured from the buyer's card until they confirm receipt of
      // the car. On cancel/dispute, the authorization is voided and
      // the buyer's card is released with zero charge.
      //
      // Prior behavior was to send only 75 SAR (the fee) which meant
      // the car price never entered the escrow flow at all — a design
      // gap Moyasar confirmed on 2026-02 must be closed via MPF.
      const totalHoldSar = Math.max(0, Math.round(effectivePrice + feeData.fee_sar));
      const params = new URLSearchParams({
        purpose: "escrow_deposit",
        amount_sar: String(totalHoldSar),
        reference_id: String(vehicleId || "general"),
      });
      navigate(`/checkout/moyasar?${params.toString()}`);
      return;
    } catch (e) {
      setError(e?.response?.data?.detail || (lang === "ar" ? "حدث خطأ غير متوقع." : "Unexpected error."));
    } finally {
      setSubmitting(false);
    }
  };

  // iter152 — /deal/:vehicleId must always render the full deal completion
  // UI so the "Complete Deal via CARTAL" button never lands users on a
  // dead-end. The `FEATURES.ESCROW_ENABLED` flag is now handled inline
  // (specific escrow wording/paths hidden) rather than blocking the page.

  return (
    <div className="max-w-[1100px] mx-auto px-5 py-10" data-testid="deal-completion-page">
      <div className="overline overline-gold mb-2"><Sparkles className="w-3 h-3 inline" /> {lang === "ar" ? "إكمال الصفقة" : "Complete the deal"}</div>
      <h1 className="font-display text-4xl sm:text-5xl mb-3">
        {lang === "ar" ? "أتمم صفقتك الآن" : "Finalize your deal now"}
      </h1>
      <p className="text-[#525252] mb-8 max-w-2xl">
        {lang === "ar"
          ? "ادفع رسوم منصة كارتال فقط، وأكمل عملية الشراء بأمان عبر ضمان كارتال. الخدمات الإضافية اختيارية تماماً."
          : "Pay only the CARTAL platform fee, and complete the purchase securely via CARTAL Escrow. The extra services are completely optional."}
      </p>

      {/* Vehicle summary */}
      {vehicle && (
        <div className="surface rounded-2xl p-4 mb-6 flex items-center gap-4" data-testid="deal-vehicle-summary">
          {vehicle.image && <img src={vehicle.image} alt="" className="w-20 h-20 object-cover rounded-lg" />}
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg truncate">{vehicle.year} {vehicle.make} {vehicle.model}</div>
            <div className="text-xs text-[#6b6b6b]">{vehicle.city}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-display text-2xl text-[#0a0a0a]" data-testid="deal-vehicle-price">
              {formatSAR(vehicle.price_sar || vehicle.price || 0)} <span className="text-xs text-[#8a8a8a]">SAR</span>
            </div>
          </div>
        </div>
      )}

      {/* Platform fee calculator + Pay CTA — HERO of the page */}
      <FeeCalculatorCard
        lang={lang}
        vehicle={vehicle}
        feeData={feeData}
        customPrice={customPrice}
        setCustomPrice={setCustomPrice}
        withEscrow={withEscrow}
        setWithEscrow={setWithEscrow}
        onPay={handlePay}
        submitting={submitting}
        error={error}
      />

      {/* Optional add-on services (no longer required) */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-2">
          <h2 className="font-display text-2xl">{lang === "ar" ? "خدمات إضافية (اختيارية)" : "Optional add-on services"}</h2>
        </div>
        <p className="text-sm text-[#6b6b6b] mb-5">
          {lang === "ar"
            ? "هذه خدمات قد تساعدك في رحلتك، لكنها ليست شرطاً لإتمام الصفقة. اختر ما تحتاجه فقط."
            : "These services may help your journey, but none are required to complete the deal. Pick only what you need."}
        </p>

        <div className="space-y-3">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            const done = completed(s.link_cat);
            return (
              <div
                key={s.id}
                className={`surface rounded-2xl p-5 ${done ? "border-2 border-green-500/40" : ""}`}
                data-testid={`deal-step-${s.id}`}
              >
                <div className="flex items-center gap-4 flex-wrap">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-display text-xl ${done ? "bg-green-100 text-green-700" : "bg-[#c9a55c]/15 text-[#8a6f2e] border-2 border-[#c9a55c]/40"}`}>
                    {done ? <CheckCircle2 className="w-6 h-6" /> : (idx + 1)}
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display text-xl">{lang === "ar" ? s.ar : s.en}</h3>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#0a0a0a]/[0.06] text-[#6b6b6b]">{lang === "ar" ? "اختياري" : "Optional"}</span>
                      {done && <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">{lang === "ar" ? "تم الطلب" : "Requested"}</span>}
                    </div>
                  </div>
                  <Link
                    to={s.link_cat === "ownership_transfer_official" ? "/ownership-transfer" : `/services/${s.link_cat}`}
                    className="btn-primary text-sm"
                    data-testid={`deal-step-${s.id}-cta`}
                  >
                    <Icon className="w-4 h-4" />
                    {done ? (lang === "ar" ? "اطلب مرة أخرى" : "Order again") : (lang === "ar" ? "اختر مزوّد" : "Pick a provider")}
                    <ArrowRight className="w-3 h-3 rtl:rotate-180" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="surface rounded-2xl p-6 mt-8 bg-gradient-to-br from-[#0a0a0a] to-[#1a1a1a] border border-[#c9a55c]/40">
        <h3 className="font-display text-xl text-[#f1ecdf] font-bold mb-2">{lang === "ar" ? "متابعة جميع طلباتك" : "Track all your orders"}</h3>
        <p className="text-[#c9a55c] text-sm mb-4">{lang === "ar" ? "تابع حالة كل خدمة من لوحة واحدة." : "Monitor every service in one place."}</p>
        <button onClick={() => navigate("/orders")} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#c9a55c] hover:bg-[#d4b56e] text-[#0a0a0a] text-sm font-bold" data-testid="goto-orders">
          {lang === "ar" ? "لوحة طلباتي" : "My orders"} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Fee calculator hero card
// ─────────────────────────────────────────────────────────────────────
function FeeCalculatorCard({ lang, vehicle, feeData, customPrice, setCustomPrice, withEscrow, setWithEscrow, onPay, submitting, error }) {
  const hasVehiclePrice = Number(vehicle?.price_sar || vehicle?.price || 0) > 0;
  const showCustomInput = !hasVehiclePrice;

  return (
    <div
      className="rounded-3xl p-7 relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #0a0a0a 0%, #1f1a10 100%)",
        border: "2px solid #c9a55c",
      }}
      data-testid="deal-fee-card"
    >
      <div className="flex items-center gap-2" style={{ color: "#d4ae6a" }}>
        <Calculator className="w-5 h-5" />
        <div className="overline" style={{ color: "#d4ae6a" }}>
          {lang === "ar" ? "خيارات إتمام الصفقة" : "Deal completion options"}
        </div>
      </div>

      <h2 className="font-display text-3xl sm:text-4xl mt-3" style={{ color: "#f7eecd" }}>
        {lang === "ar" ? "اختر طريقة إتمام الصفقة" : "Choose how to complete the deal"}
      </h2>
      <p className="text-sm mt-2 max-w-2xl" style={{ color: "rgba(232,225,207,0.78)" }}>
        {lang === "ar"
          ? "كارتال تقدم لك خيارين: ضمان كامل مقابل 75 ر.س فقط بمناسبة الإطلاق (بدلاً من 150)، أو إتمام الصفقة مباشرة مع البائع مجاناً."
          : "CARTAL offers two paths: full escrow protection for only 75 SAR (launch price, was 150 SAR), or a direct deal with the seller — free."}
      </p>

      {/* ── Escrow toggle ───────────────────────────────────────────── */}
      <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="escrow-toggle">
        <button
          type="button"
          onClick={() => setWithEscrow(true)}
          className="relative text-start rounded-2xl p-4 transition-all overflow-hidden"
          style={{
            background: withEscrow ? "rgba(201,165,92,0.18)" : "rgba(255,255,255,0.04)",
            border: withEscrow ? "2px solid #c9a55c" : "1px solid rgba(255,255,255,0.1)",
          }}
          data-testid="escrow-option-with"
        >
          {/* Launch discount corner ribbon — always visible on the escrow tile */}
          <div
            className="absolute -top-1 -end-1 z-10 rounded-bl-2xl rounded-tr-2xl px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider shadow-lg"
            style={{
              background: "linear-gradient(135deg,#ef4444 0%,#b91c1c 100%)",
              color: "#ffffff",
              border: "1px solid rgba(255,255,255,0.25)",
              transform: "translateZ(0)",
            }}
            data-testid="escrow-launch-discount-ribbon"
          >
            {lang === "ar" ? "خصم 50٪ · إطلاق" : "50% OFF · LAUNCH"}
          </div>

          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" style={{ color: withEscrow ? "#c9a55c" : "#8a8a8a" }} />
            <div className="font-bold text-base" style={{ color: "#f7eecd" }}>
              {lang === "ar" ? "مع ضمان كارتال" : "With CARTAL Escrow"}
            </div>
            <span className="ms-auto text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: "#c9a55c", color: "#0a0a0a" }}>
              {lang === "ar" ? "الأكثر أماناً" : "Most secure"}
            </span>
          </div>
          <div className="text-[12px] mt-1.5 leading-snug" style={{ color: "rgba(232,225,207,0.75)" }}>
            {lang === "ar"
              ? "يتم حجز مبلغ السيارة عبر ميسر حتى تستلم السيارة وتؤكّد رضاك. دعم نزاعات كامل."
              : "The car amount is authorized via Moyasar until you receive the car and confirm satisfaction. Full dispute support."}
          </div>
          {/* Price row — 75 SAR new, 150 SAR crossed out, 50% off pill */}
          <div className="mt-2 flex items-baseline gap-2 flex-wrap">
            <div className="inline-flex items-baseline gap-1 font-display text-2xl font-bold" style={{ color: withEscrow ? "#f7eecd" : "rgba(247,238,205,0.75)" }}>
              75 <span className="text-xs font-normal" style={{ color: "rgba(232,225,207,0.7)" }}>SAR</span>
            </div>
            <span
              className="font-display text-base line-through decoration-2"
              style={{ color: "rgba(232,225,207,0.45)", textDecorationColor: "#ef4444" }}
              data-testid="escrow-original-price"
            >
              150
            </span>
            <span
              className="inline-flex items-center gap-1 text-[10px] font-extrabold rounded-full px-2 py-0.5"
              style={{ background: "rgba(239,68,68,0.20)", color: "#fecaca", border: "1px solid rgba(239,68,68,0.55)" }}
              data-testid="escrow-savings-pill"
            >
              {lang === "ar" ? "وفّرت 75 ر.س" : "Save 75 SAR"}
            </span>
          </div>
          <div className="mt-1 text-[10px] font-bold" style={{ color: "#fca5a5" }}>
            {lang === "ar" ? "عرض محدود بمناسبة إطلاق كارتال" : "Limited-time CARTAL launch offer"}
          </div>
        </button>

        <button
          type="button"
          onClick={() => setWithEscrow(false)}
          className="text-start rounded-2xl p-4 transition-all"
          style={{
            background: !withEscrow ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
            border: !withEscrow ? "2px solid rgba(232,225,207,0.6)" : "1px solid rgba(255,255,255,0.1)",
          }}
          data-testid="escrow-option-without"
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" style={{ color: !withEscrow ? "#d4ae6a" : "#8a8a8a" }} />
            <div className="font-bold text-base" style={{ color: "#f7eecd" }}>
              {lang === "ar" ? "بدون ضمان (مباشرة مع البائع)" : "Without escrow (direct deal)"}
            </div>
            <span className="ms-auto text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: "rgba(34,197,94,0.25)", color: "#86efac" }}>
              {lang === "ar" ? "مجاني" : "FREE"}
            </span>
          </div>
          <div className="text-[12px] mt-1.5 leading-snug" style={{ color: "rgba(232,225,207,0.75)" }}>
            {lang === "ar"
              ? "تتم الصفقة مباشرة بينك والبائع. كارتال ليست طرفاً ولا تتحمّل أي مسؤولية تجاه هذه المعاملة."
              : "Deal happens directly between you and the seller. CARTAL is not a party and bears no responsibility."}
          </div>
          <div className="mt-2 font-display text-xl font-bold" style={{ color: !withEscrow ? "#86efac" : "rgba(134,239,172,0.75)" }}>
            0 <span className="text-xs font-normal" style={{ color: "rgba(232,225,207,0.7)" }}>SAR</span>
          </div>
        </button>
      </div>

      {/* Manual price input (only when vehicle price is unknown / general flow) */}
      {showCustomInput && (
        <div className="mt-5 max-w-md">
          <label className="text-xs font-semibold mb-1 block" style={{ color: "#d4ae6a" }}>
            {lang === "ar" ? "قيمة السيارة (SAR)" : "Vehicle value (SAR)"}
          </label>
          <input
            type="number"
            inputMode="numeric"
            min="0"
            value={customPrice}
            onChange={(e) => setCustomPrice(e.target.value)}
            placeholder={lang === "ar" ? "مثال: 75000" : "e.g. 75000"}
            className="w-full px-4 py-3 rounded-xl text-lg font-display"
            style={{ background: "#1a1a1a", color: "#f1ecdf", border: "1px solid #c9a55c40" }}
            data-testid="deal-fee-price-input"
          />
        </div>
      )}

      {/* Fee breakdown */}
      {feeData ? (
        <>
          {/* No-escrow disclaimer */}
          {!withEscrow && (
            <div
              className="mt-5 rounded-2xl p-4 flex items-start gap-3"
              style={{ background: "rgba(234,179,8,0.12)", border: "1px solid rgba(234,179,8,0.45)" }}
              data-testid="no-escrow-disclaimer"
            >
              <AlertCircle className="w-6 h-6 shrink-0" style={{ color: "#facc15" }} />
              <div className="flex-1 text-sm" style={{ color: "#fef3c7" }}>
                <div className="font-bold mb-1" style={{ color: "#fde68a" }}>
                  {lang === "ar" ? "تنبيه — صفقة مباشرة بدون ضمان" : "Notice — direct deal without escrow"}
                </div>
                <div className="text-xs leading-relaxed" style={{ color: "rgba(254,243,199,0.85)" }}>
                  {lang === "ar"
                    ? "اخترتَ إتمام الصفقة مباشرةً مع البائع. كارتال ليست طرفاً في هذه المعاملة، ولا تتحمّل أي مسؤولية عن الدفع، التسليم، النزاعات، أو حالة المركبة. ننصح بشدة بفحص السيارة قبل الدفع، والاجتماع في مكان عام، ونقل الملكية فوراً."
                    : "You chose to deal directly with the seller. CARTAL is NOT a party to this transaction and bears no responsibility for payment, delivery, disputes, or vehicle condition. We strongly recommend inspecting the car before paying, meeting in a public place, and transferring ownership immediately."}
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 grid sm:grid-cols-3 gap-3" data-testid="deal-fee-breakdown">
            <FeeStat
              label={lang === "ar" ? "قيمة السيارة" : "Car value"}
              value={`${formatSAR(feeData.price_sar)} SAR`}
              sub={lang === "ar" ? "تُدفع مباشرة للبائع" : "Paid directly to seller"}
              testid="fee-stat-price"
            />
            <FeeStat
              label={lang === "ar" ? "رسوم الضمان" : "Escrow fee"}
              value={feeData.fee_sar === 0 ? (lang === "ar" ? "بدون" : "None") : `${formatSAR(feeData.fee_sar)} SAR`}
              highlight
              sub={feeData.fee_sar === 0
                ? (lang === "ar" ? "اخترتَ بدون ضمان" : "You opted out")
                : (lang === "ar" ? "ثابتة لكل الصفقات" : "Flat, all deals")}
              testid="fee-stat-fee"
            />
            <FeeStat
              label={lang === "ar" ? "ما تدفعه لكارتال الآن" : "Pay CARTAL now"}
              value={`${formatSAR(feeData.fee_sar)} SAR`}
              big
              testid="fee-stat-total"
            />
          </div>
        </>
      ) : (
        <div className="mt-6 inline-flex items-center gap-2 text-sm" style={{ color: "rgba(232,225,207,0.7)" }}>
          <AlertCircle className="w-4 h-4" />
          {lang === "ar" ? "أدخل قيمة السيارة لاستعراض الخيارات." : "Enter the car value to view options."}
        </div>
      )}

      {/* Pay CTA */}
      <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <button
          type="button"
          onClick={onPay}
          disabled={submitting || !feeData}
          className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-base font-bold transition-all shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: "linear-gradient(135deg,#d4b56e 0%,#c9a55c 60%,#8a6f2e 100%)", color: "#0a0a0a" }}
          data-testid="deal-pay-now-btn"
        >
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
          {withEscrow
            ? (lang === "ar" ? "تفعيل الضمان وإتمام الصفقة" : "Activate escrow & complete deal")
            : (lang === "ar" ? "تواصل مع البائع مباشرة" : "Contact seller directly")}
          <ArrowRight className="w-4 h-4 rtl:rotate-180" />
        </button>
        <p className="text-xs leading-relaxed flex-1" style={{ color: "rgba(232,225,207,0.7)" }}>
          {withEscrow
            ? (lang === "ar"
                ? "يتم حجز المبلغ (75 ر.س — بعد خصم 50٪ الإطلاق) على بطاقتك عبر ميسر بصلاحية «تفويض مسبق» ولن يُخصم فعلياً حتى تؤكّد استلامك للسيارة، أو يُرفع الحجز تلقائياً إذا ألغيت الصفقة."
                : "The 75 SAR (50% launch discount applied) is authorised (held) on your card via Moyasar and NOT charged until you confirm receipt of the car — or released back if you cancel the deal.")
            : (lang === "ar"
                ? "لا تدفع شيئاً لكارتال. ستفتح صفحة الإعلان للتواصل مع البائع مباشرة."
                : "No payment to CARTAL. You'll be taken to the listing to contact the seller directly.")}
        </p>
      </div>

      {error && (
        <div className="mt-4 px-4 py-3 rounded-xl text-sm bg-red-500/15 text-red-200 border border-red-500/30" data-testid="deal-pay-error">
          {error}
        </div>
      )}
    </div>
  );
}

function FeeStat({ label, value, sub, strike, highlight, big, testid }) {
  return (
    <div
      className="rounded-xl p-4"
      style={{
        background: highlight ? "rgba(201,165,92,0.16)" : "rgba(255,255,255,0.04)",
        border: highlight ? "1px solid #c9a55c80" : "1px solid rgba(255,255,255,0.08)",
      }}
      data-testid={testid}
    >
      <div className="text-[11px] uppercase tracking-widest" style={{ color: "rgba(232,225,207,0.7)" }}>
        {label}
      </div>
      {strike && (
        <div
          className="text-xs line-through mt-1"
          style={{ color: "rgba(232,225,207,0.55)" }}
          data-testid={`${testid}-strike`}
        >
          {strike}
        </div>
      )}
      <div
        className={`font-display mt-1 ${big ? "text-3xl" : "text-2xl"}`}
        style={{ color: highlight || big ? "#f7eecd" : "#e8e1cf" }}
      >
        {value}
      </div>
      {sub && <div className="text-[10px] mt-1" style={{ color: "#d4ae6a" }}>{sub}</div>}
    </div>
  );
}
